import React from 'react';
import { useParams } from '@framework/next/navigation';
import { set } from 'date-fns';
import cn from 'classnames';
import Input from '@dealroadshow/uikit-input';
import Label from '@dealroadshow/uikit-input/Label';
import { RadioGroup, Radio } from '@dealroadshow/uikit-input/Radio';
import IconClock from '@dealroadshow/uikit-icon/IconClock';

import { ICallDetailsFormValues } from './interfaces';
import { IDialInCountry } from '@evercall/domain/vo/DialInCountry';
import { ITransactionType } from '@evercall/domain/vo/TransactionType';
import { IPlatform } from '@evercall/domain/vo/Platform';

import {
  CALL_SCHEDULE_DISCLAIMER,
  EVERCALL_EVENT_TYPE_OPTIONS,
  TIME_INTERVAL,
  WEBINAR_SCHEDULE_DISCLAIMER,
} from './constants';
import { TIME_FORMAT_12, WEEKDAY_MONTH_DATE_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import CallEvent from '@evercall/domain/CallEvent';
import { CallEventType } from '@evercall/domain/vo/call/CallEventType';

import DatePicker from '@dealroadshow/uikit-picker/DatePicker';
import BaseDatePicker from '@dealroadshow/uikit-picker/BaseDatePicker';
import { Field, useForm } from '@framework/ui/Organisms/FinalForm';
import { Select } from '@framework/ui/Organisms/FinalForm/Fields/Select';
import DialInCountriesFieldArray from './DialInCountriesFieldArray';
import SpeakersTableFieldArray from './SpeakersTableFieldArray';

import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';
import getDateFromUnixTime from '@framework/date-helpers/getDateFromUnixTime';
import { getScheduleDemoFormTimeValues } from '@framework/ui/Organisms/ScheduleDemo/ScheduleDemoForm/helpers';
import { ITimeZoneOption } from '@framework/timezone/helpers';
import { mapOptionToValue } from '@framework/data-helpers/selectOptionMappers';
import useCallDetailsOptions from './useCallDetailsOptions';
import { useCallFormContext } from './CallFormContext';

import styles from './callForm.module.scss';

interface IProps {
  // classNames
  fieldsContainerCls?: string,
  dialInCountriesContainerCls?: string,
  dialInCountriesLabelCls?: string,
  // lists
  timeZoneOptions: ITimeZoneOption[],
  transactionTypeList: ITransactionType[],
  platformList?: IPlatform[],
  countryList: IDialInCountry[],
  // form
  disabledFields?: {
    estimatedDuration: boolean,
    approximateNumberOfParticipants: boolean,
    dialInCountries: boolean,
    webinarPlatformId: boolean,
    eventTypeId: boolean,
    speakers?: boolean,
  },
}

const CallDetailsFields = (
  {
    // classNames
    fieldsContainerCls,
    dialInCountriesContainerCls,
    dialInCountriesLabelCls,
    // lists
    timeZoneOptions,
    transactionTypeList,
    platformList,
    countryList,
    // form
    disabledFields = {
      estimatedDuration: false,
      approximateNumberOfParticipants: false,
      dialInCountries: false,
      webinarPlatformId: false,
      eventTypeId: false,
      speakers: false,
    },
  }: IProps,
) => {
  const { isEvercall } = useCallFormContext();

  const normalizeIntegerInputs = (value) => (!value ? value : value.replace(/[^\d]/g, ''));

  const {
    platformOptions,
    transactionTypeOptions,
  } = useCallDetailsOptions({
    transactionTypeList,
    platformList,
  });

  const form = useForm<ICallDetailsFormValues>();
  const { values } = form.getState();

  const {
    now,
    timeAtFieldMinTimeValue,
  } = getScheduleDemoFormTimeValues({
    dateAtFormValue: values.dateAt,
    // @ts-ignore
    timeZoneFormValue: values.timeZoneId,
  });

  const eventTypeId = mapOptionToValue(values?.eventTypeId);
  const isWebinar = CallEvent.isWebinar(eventTypeId as CallEventType);
  const isMeeting = CallEvent.isMeeting(eventTypeId as CallEventType);
  // useParams is null outside the App Router: apps/evercall renders this form on /schedule.
  const { callId } = useParams<{ callId?: string }>() ?? {};
  const callIdFromUrl = callId?.toString();

  return (
    <>
      <div className={ cn(styles.inputWrapper, fieldsContainerCls) }>
        { !isEvercall ? (
          <div className={ styles.bigInput }>
            <Field
              name="eventTypeId"
              label="Event Type"
              placeholder="Select Event Type"
              isDisabled={ disabledFields.eventTypeId || !!callIdFromUrl }
              isClearable={ false }
              component={ Select }
              options={ EVERCALL_EVENT_TYPE_OPTIONS }
              dataTest="eventTypeIdSelect"
            />
          </div>
        ) : (
          <div className={ styles.bigInput }>
            <Field
              name="eventTypeId"
              component={ ({ input }) => (
                <RadioGroup
                  { ...input }
                  selectedValue={ input.value }
                  className={ styles.eventTypesButtonGroup }
                  dataTest="eventTypeIdRadioGroup"
                  onChange={ (selectedValue: CallEventType) => input.onChange(
                    EVERCALL_EVENT_TYPE_OPTIONS.find((option) => option.value === selectedValue),
                  ) }
                >
                  <Radio
                    label={ EVERCALL_EVENT_TYPE_OPTIONS[0].label }
                    value={ EVERCALL_EVENT_TYPE_OPTIONS[0].value }
                    radioLabelClassName={ cn(styles.eventTypesRadioLabel, { [styles.inactive]: isWebinar }) }
                    className={ cn(styles.eventTypesButton, { [styles.inactive]: isWebinar }) }
                    dataTest={ `eventTypeId${ EVERCALL_EVENT_TYPE_OPTIONS[0].value }Button` }
                  />
                  <Radio
                    label={ EVERCALL_EVENT_TYPE_OPTIONS[1].label }
                    value={ EVERCALL_EVENT_TYPE_OPTIONS[1].value }
                    radioLabelClassName={ cn(styles.eventTypesRadioLabel, { [styles.inactive]: !isWebinar }) }
                    className={ cn(styles.eventTypesButton, { [styles.inactive]: !isWebinar }) }
                    dataTest={ `eventTypeId${ EVERCALL_EVENT_TYPE_OPTIONS[1].value }Button` }
                  />
                </RadioGroup>
              ) }
            />
          </div>
        ) }
        <div className={ styles.bigInput }>
          <Field
            name="name"
            label="Event Title (To Be Introduced by Moderator)"
            component={ Input }
            placeholder="eg Acme Global Investor Event"
            dataTest="detailFormCallNameInput"
          />
        </div>
        { (isWebinar || isMeeting) && (
          <div className={ styles.bigInput }>
            <Field
              name={ isMeeting ? 'managedMeetingPlatformId' : 'webinarPlatformId' }
              label={ `${ isMeeting ? '' : 'Stream ' }Platform` }
              placeholder="Select Stream Platform"
              isDisabled={ disabledFields.webinarPlatformId }
              isClearable={ false }
              component={ Select }
              options={ platformOptions }
              dataTest="webinarPlatformIdSelect"
            />
          </div>
        ) }
        <div className={ styles.bigInput }>
          <Field
            name="transactionTypeId"
            label="Transaction Type"
            placeholder="Select Transaction Type"
            component={ Select }
            isClearable={ false }
            options={ transactionTypeOptions }
            dataTest="transactionTypesEvercallScheduleCallSelect"
          />
        </div>
        <div className={ styles.datePickerInput }>
          <Field
            name="dateAt"
            component={ ({
              meta,
              input,
            }) => (
              <DatePicker
                label="Event Date"
                value={ getDateFromUnixTime(input.value) }
                onChange={ (value) => value && input.onChange(getUnixTimestamp(value)) }
                meta={ meta }
                placeholder="Select Date"
                dateFormat={ WEEKDAY_MONTH_DATE_FORMAT }
                name={ input.name }
                isReadOnly
                isClearable={ false }
                dataTest="evercallScheduleCallDateAtPicker"
                inputDataTest="detailFormCallDateInput"
                { ...(isEvercall && { minDate: now }) }
                isNarrow
              />
            ) }
          />
        </div>
        <div className={ styles.smallInput }>
          <Field
            name="estimatedDuration"
            disabled={ disabledFields.estimatedDuration }
            label="EST. DURATION (IN MIN)"
            component={ Input }
            placeholder="eg 60"
            parse={ normalizeIntegerInputs }
            isClearable={ false }
            dataTest="detailFormDurationInput"
          />
        </div>
        <div className={ styles.datePickerInput }>
          <Field
            name="timeAt"
            component={ ({
              input,
              meta,
            }) => (
              (
                <>
                  <Label htmlFor="timeAt">
                    Event Time
                  </Label>
                  <BaseDatePicker
                    usePortal
                    popperClassName={ styles.timeAtDatePickerArrowPopperClassName }
                    showTimeSelect
                    showTimeSelectOnly
                    placeholderText="Select Time"
                    timeIntervals={ TIME_INTERVAL }
                    dateFormat={ TIME_FORMAT_12 }
                    selected={ getDateFromUnixTime(input.value) }
                    onChange={ (value: Date) => input.onChange(getUnixTimestamp(value)) }
                    dataTest="evercallScheduleCallTimeAtPicker"
                    customInput={ (
                      <Input
                        isNarrow
                        icon={ IconClock }
                        // @ts-ignore
                        input={ { name: input.name } }
                        meta={ meta }
                        // @ts-ignore
                        iconPosition="right"
                        isReadOnly
                        isClearable={ false }
                        dataTest="detailFormCallTimeInput"
                      />
                    ) }
                    { ...(isEvercall && (
                        {
                          minTime: timeAtFieldMinTimeValue,
                          maxTime: set(now, {
                            hours: 23,
                            minutes: 46,
                          }),
                        })
                    ) }
                  />
                </>
              )
            ) }
          />
        </div>
        <div className={ styles.smallInput }>
          <Field
            label="Event Time Zone"
            name="timeZoneId"
            component={ Select }
            options={ timeZoneOptions }
            selectedPlaceholderWhenOpen="Time Zone"
            isClearable={ false }
            isSearchable
            isSelectedPlaceholderVisibleWhenOpen
            dataTest="timezoneSelect"
          />
        </div>
        <div className={ styles.bigInput }>
          <Field
            isNarrow={ isMeeting }
            name="approximateNumberOfParticipants"
            disabled={ disabledFields.approximateNumberOfParticipants }
            label="approximate # of participants (including speakers)"
            component={ Input }
            placeholder="1-1000"
            parse={ normalizeIntegerInputs }
            isClearable={ false }
            dataTest="detailFormParticipantsCountInput"
          />
        </div>
      </div>
      { !isMeeting && (
        <DialInCountriesFieldArray
          countryList={ countryList }
          disabled={ disabledFields.dialInCountries }
          tableContainerCls={ dialInCountriesContainerCls }
          labelCls={ dialInCountriesLabelCls }
        />
      ) }
      { !isEvercall && !isMeeting && (
        <div className={ styles.disclaimerLabel }>
          { isWebinar ? WEBINAR_SCHEDULE_DISCLAIMER : CALL_SCHEDULE_DISCLAIMER }
        </div>
      ) }
      { isWebinar && (
        <SpeakersTableFieldArray disabled={ disabledFields.speakers } />
      ) }
    </>
  );
};

export default CallDetailsFields;
